/**
 * ChatGame API Client for Frontend
 * Handles all communication with the ChatGame backend
 */

import { API_BASE_URL } from '../../config/api';
import { trackEvents, analytics } from '../../utils/analytics';

class ChatGameApiClient {
    constructor(baseUrl = API_BASE_URL) {
        this.baseUrl = baseUrl;
    }

    /**
     * Store player_secret in localStorage for a given player_id.
     * 🧯 #9c (10-01): never throws. A blocked or full storage (private mode, a quota) threw
     * AFTER the room already existed, so create said "That didn't work" and a retry made a
     * SECOND room. Now the create goes through; this device just can't keep the key (one warn).
     */
    storePlayerSecret(playerId, playerSecret) {
        if (playerId && playerSecret) {
            try {
                localStorage.setItem(`player_secret_${playerId}`, playerSecret);
            } catch (e) {
                if (!this._secretStoreWarned) {
                    this._secretStoreWarned = true;
                    console.warn(`[ChatGameApi] could not save a seat key on this device (${(e && e.name) || 'storage error'})`);
                }
            }
        }
    }

    /**
     * Retrieve player_secret from localStorage for a given player_id
     */
    getPlayerSecret(playerId) {
        return playerId ? localStorage.getItem(`player_secret_${playerId}`) : null;
    }

    /**
     * Create a new chat game (async pattern - uses job queue)
     * @param {Object} config - Game configuration
     * @param {Function} onProgress - Optional callback for progress updates (progress, message)
     * @returns {Promise<Object>} Game creation result
     */
    async createGame(config, onProgress = null) {
        // Submit the async job
        const submitResponse = await fetch(`${this.baseUrl}/api/async/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'createChatGame',
                params: config
            })
        });

        if (!submitResponse.ok) {
            const error = await submitResponse.json();
            throw new Error(error.error || 'Failed to submit game creation job');
        }

        const { jobId } = await submitResponse.json();
        console.log(`[ChatGameApi] Job submitted: ${jobId}`);

        // Poll for completion
        return this.pollJobStatus(jobId, onProgress);
    }

    /**
     * Poll job status until complete or error
     * @param {string} jobId - The job ID to poll
     * @param {Function} onProgress - Optional callback for progress updates
     * @param {number} pollInterval - Poll interval in ms (default: 2000)
     * @param {number} maxWaitTime - Max wait time in ms (default: 5 minutes)
     * @returns {Promise<Object>} Job result
     */
    async pollJobStatus(jobId, onProgress = null, pollInterval = 2000, maxWaitTime = 300000) {
        const startTime = Date.now();

        while (true) {
            // [5g19-trace] same pattern as getGameStatus. The throw exits the
            // while loop so this logs at most ONCE per pollJobStatus call (not
            // per iteration). Captures cascade hits on /api/job/{id} polling.
            const url = `${this.baseUrl}/api/job/${jobId}`;
            const t0 = Date.now();
            let response;
            try {
                response = await fetch(url);
            } catch (e) {
                console.error(`[5g19-trace] pollJobStatus THREW name=${e.name} msg=${e.message} url=${url} dt=${Date.now()-t0}ms jobId=${jobId}`);
                throw e;
            }

            if (!response.ok) {
                console.warn(`[5g19-trace] pollJobStatus status=${response.status} url=${url} dt=${Date.now()-t0}ms jobId=${jobId}`);
                const error = await response.json();
                throw new Error(error.error || 'Failed to get job status');
            }

            const status = await response.json();
            console.log(`[ChatGameApi] Job ${jobId} status: ${status.status} (${status.progress}%)`);

            // Call progress callback if provided
            if (onProgress && typeof onProgress === 'function') {
                onProgress(status.progress, status.message);
            }

            if (status.status === 'complete') {
                return status.result;
            }

            if (status.status === 'error') {
                // Handle various error response formats
                const errorMessage = typeof status.error === 'string'
                    ? status.error
                    : status.error?.message || status.message || 'Game operation failed';
                throw new Error(errorMessage);
            }

            // Check timeout
            if (Date.now() - startTime > maxWaitTime) {
                throw new Error('Game creation timed out');
            }

            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
    }

    /**
     * Create a new chat game (sync/direct call - for backwards compatibility)
     * @param {Object} config - Game configuration
     * @returns {Promise<Object>} Game creation result
     */
    async createGameSync(config) {
        const response = await fetch(`${this.baseUrl}/chat-game/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(config)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create game');
        }

        return response.json();
    }

    /**
     * Progress the game by one step (async pattern - uses job queue)
     * @param {string} gameId - Game ID
     * @param {Function} onProgress - Optional callback for progress updates (progress, message)
     * @returns {Promise<Object>} Progress result
     */
    async progressGame(gameId, onProgress = null) {
        // Submit the async job
        const submitResponse = await fetch(`${this.baseUrl}/api/async/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'progressChatGame',
                params: { game_id: gameId }
            })
        });

        if (!submitResponse.ok) {
            const error = await submitResponse.json();
            throw new Error(error.error || 'Failed to submit game progress job');
        }

        const { jobId } = await submitResponse.json();
        console.log(`[ChatGameApi] Progress job submitted: ${jobId}`);

        // Poll for completion
        return this.pollJobStatus(jobId, onProgress);
    }

    /**
     * Progress the game by one step (sync/direct call - for backwards compatibility)
     * @param {string} gameId - Game ID
     * @returns {Promise<Object>} Progress result
     */
    async progressGameSync(gameId) {
        const response = await fetch(`${this.baseUrl}/chat-game/progress`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ game_id: gameId })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to progress game');
        }

        return response.json();
    }

    /**
     * Get current game status
     * @param {string} gameId - Game ID
     * @param {string} playerId - Optional player ID
     * @returns {Promise<Object>} Game status
     */
    async getGameStatus(gameId, playerId = null, opts = {}) {
        const url = new URL(`${this.baseUrl}/chat-game/status`);
        url.searchParams.append('game_id', gameId);
        // 🔒 Private-room reads (09-07, audit C1): an unshared ported room only
        // serves seat-holders once PRIVATE_ROOM_READS_ENFORCE flips, so reads
        // now carry the seat identity. Fall back to the stored seat for this
        // game — the page's very first status fetch fires before humanPlayerId
        // state is seeded, which used to send no player_id at all.
        let effectiveId = playerId;
        if (!effectiveId) {
            try { effectiveId = localStorage.getItem(`humanPlayerId_${gameId}`) || null; } catch (e) { /* non-browser env */ }
        }
        if (effectiveId) {
            url.searchParams.append('player_id', effectiveId);
            const secret = this.getPlayerSecret(effectiveId);
            if (secret) url.searchParams.append('player_secret', secret);
        }
        // Presence hint for the backend's ambient lanes (host TV, quips):
        // a hidden tab is not a viewer — browsers keep firing throttled polls
        // (~1/min) from background tabs, which would otherwise keep the space's
        // ambient AI running for an abandoned tab. Backend treats a MISSING
        // param as visible so stale cached clients keep working (fail-open).
        try {
            url.searchParams.append('viewer_active', (typeof document !== 'undefined' && document.visibilityState === 'hidden') ? '0' : '1');
        } catch (e) { /* non-browser env */ }
        // watching=1: the watch-party theater is actually rendered on screen
        // right now — the backend stamps theater presence off this and gates
        // the quip lane on it. Missing param = fail-closed (no quips).
        if (opts.watching) {
            url.searchParams.append('watching', '1');
        }

        // [5g19-trace] same instrumentation pattern as skipReadyCheck etc.
        // — see Cycle 5g.19 refire #2 + 2026-06-13 validate smoke: this method
        // failed with the same TypeError fingerprint as the instrumented ones,
        // but was uninstrumented so we couldn't capture URL/dt/error.name.
        // Adds diagnostic surface for the cascade investigation.
        const t0 = Date.now();
        let response;
        try {
            response = await fetch(url);
        } catch (e) {
            console.error(`[5g19-trace] getGameStatus THREW name=${e.name} msg=${e.message} url=${url.toString()} dt=${Date.now()-t0}ms gameId=${gameId}`);
            throw e;
        }

        if (!response.ok) {
            console.warn(`[5g19-trace] getGameStatus status=${response.status} url=${url.toString()} dt=${Date.now()-t0}ms gameId=${gameId}`);
            let errorMsg = `Failed to get game status (${response.status})`;
            try {
                const error = await response.json();
                errorMsg = error.error || errorMsg;
            } catch (e) {
                // Response body wasn't valid JSON (e.g. 504 gateway timeout)
            }
            throw new Error(errorMsg);
        }

        return response.json();
    }

    /**
     * Submit human player action
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID
     * @param {Object} action - Action data
     * @returns {Promise<Object>} Submission result
     */
    async submitAction(gameId, playerId, action) {
        const response = await fetch(`${this.baseUrl}/chat-game/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                action
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to submit action');
        }

        return response.json();
    }

    /**
     * Resolve a Dice Powers crit (Increment 3). Piggybacks on /chat-game/submit via an
     * `apply_power` body (the stack is at the CFN resource cap → no new route).
     * @param {string} gameId
     * @param {string} playerId
     * @param {Object} pick - { power_id, target_id?, puppet_text? }
     * @returns {Promise<Object>} { success, your_power }
     */
    async applyPower(gameId, playerId, pick) {
        const response = await fetch(`${this.baseUrl}/chat-game/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                apply_power: pick
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to apply power');
        }

        return response.json();
    }

    /**
     * Whodunnit STAGE 1: INVESTIGATE actions. Piggybacks on /chat-game/submit via an
     * `investigation` body (same CFN-cap pattern as apply_power).
     * @param {string} gameId
     * @param {string} playerId
     * @param {Object} payload - { op: 'witness'|'clue'|'ready', witness_id?, question?, clue_id? }
     * @returns {Promise<Object>} witness: { reply, questions_left } · clue: { evidence_line, ... } · ready: { ready }
     */
    async investigationAction(gameId, playerId, payload) {
        const response = await fetch(`${this.baseUrl}/chat-game/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                investigation: payload
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Investigation action failed');
        }

        return response.json();
    }

    /**
     * Get paginated messages for lazy loading older messages
     * @param {string} gameId - Game ID
     * @param {string} playerId - Optional player ID (for private message visibility)
     * @param {Object} options - Pagination options
     * @param {string} options.before - Load messages before this action ID (cursor)
     * @param {number} options.limit - Number of messages to load (default 100)
     * @param {string} options.type - 'chat' for regular messages, 'spectating' for ghost chat, or null for all
     * @returns {Promise<Object>} { messages, has_more, oldest_id }
     */
    async getMessages(gameId, playerId = null, options = {}) {
        const url = new URL(`${this.baseUrl}/chat-game/messages`);
        url.searchParams.append('game_id', gameId);
        // 🔒 Same private-room read identity as getGameStatus (09-07).
        let effectiveId = playerId;
        if (!effectiveId) {
            try { effectiveId = localStorage.getItem(`humanPlayerId_${gameId}`) || null; } catch (e) { /* non-browser env */ }
        }
        if (effectiveId) {
            url.searchParams.append('player_id', effectiveId);
            const secret = this.getPlayerSecret(effectiveId);
            if (secret) url.searchParams.append('player_secret', secret);
        }
        if (options.before) url.searchParams.append('before', options.before);
        if (options.limit) url.searchParams.append('limit', String(options.limit));
        if (options.type) url.searchParams.append('type', options.type);

        const response = await fetch(url);

        if (!response.ok) {
            let errorMsg = `Failed to get messages (${response.status})`;
            try {
                const error = await response.json();
                errorMsg = error.error || errorMsg;
            } catch (e) {}
            throw new Error(errorMsg);
        }

        return response.json();
    }

    /**
     * Get full game transcript
     * @param {string} gameId - Game ID
     * @returns {Promise<Object>} Game transcript
     */
    async getTranscript(gameId) {
        const url = new URL(`${this.baseUrl}/chat-game/transcript`);
        url.searchParams.append('game_id', gameId);

        const response = await fetch(url);

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get transcript');
        }

        return response.json();
    }

    /**
     * Share content (e.g. YouTube video) into the game action stream
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID
     * @param {string} url - Content URL (YouTube embed URL, image URL, etc.)
     * @param {string} contentType - Type of content ('youtube', 'link', 'image_url', 'video_url')
     * @returns {Promise<Object>} Submission result
     */
    async shareContent(gameId, playerId, url, contentType = 'youtube') {
        trackEvents.contentShared({ spaceId: gameId, contentType });
        const response = await fetch(`${this.baseUrl}/chat-game/share-content`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                url,
                content_type: contentType
            })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to share content');
        }

        return response.json();
    }

    /**
     * Watch party control — start/control/heartbeat/queue_add/next/stop.
     * Rides the generic /api dispatcher (POST body flag), not a dedicated route.
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID
     * @param {string} action - start|control|heartbeat|queue_add|next|stop
     * @param {Object} params - action params (url, state, position_s)
     */
    async watchParty(gameId, playerId, action, params = {}) {
        const response = await fetch(`${this.baseUrl}/api`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                watchPartyControl: true,
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                action,
                ...params
            })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(result.error || 'Watch party request failed');
        }
        return result;
    }

    /**
     * Upload a file (image, video, PDF) as shared content
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID
     * @param {File} file - File object from drag/paste
     * @returns {Promise<Object>} Submission result with uploaded URL
     */
    async shareFile(gameId, playerId, file, opts = null) { // 📎 10-02 room docs: + opts ({ knowledge })
        // 7MB raw cap: base64 inflates by 4/3 and the API gateway rejects
        // payloads over 10MB
        if (file.size > 7 * 1024 * 1024) {
            throw new Error('File too large. Maximum size is 7MB.');
        }
        const response = await fetch(`${this.baseUrl}/chat-game/share-file`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                filename: file.name || 'upload',
                mime_type: file.type || '',
                file_b64: await this._blobToBase64(file),
                // 📎 10-02 room docs: { knowledge: true } asks the room to keep this doc's
                // opening in its AIs' prompts (backend roomDocs.js); every other upload sends
                // the same body as before
                ...(opts && opts.knowledge ? { knowledge: 1 } : {}),
            })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to upload file');
        }

        return response.json();
    }

    // Files ride as base64 inside JSON — multipart binary through the API
    // gateway gets UTF-8 mangled (no binaryMediaTypes configured), which
    // corrupted every stored PNG/PDF and left the AIs unable to see them.
    async _blobToBase64(blob) {
        const bytes = new Uint8Array(await blob.arrayBuffer());
        let bin = '';
        const CHUNK = 0x8000; // String.fromCharCode arg-count limit
        for (let i = 0; i < bytes.length; i += CHUNK) {
            bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        return btoa(bin);
    }

    /**
     * Share a chat-canvas drawing: PNG raster + vector strokes (source of truth
     * for crisp re-render and future AI stroke additions).
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID
     * @param {Blob} pngBlob - Rasterized drawing
     * @param {Array} strokes - [{color, size, points: [[x,y],...]}, ...]
     * @returns {Promise<Object>} Submission result
     */
    async shareDrawing(gameId, playerId, pngBlob, strokes, drawsOn = null) {
        trackEvents.contentShared({ spaceId: gameId, contentType: 'drawing' });
        const payload = {
            game_id: gameId,
            player_id: playerId,
            player_secret: this.getPlayerSecret(playerId),
            // Doodle chain: this drawing continues an existing one ("Draw on this")
            ...(drawsOn ? { draws_on: drawsOn } : {}),
            filename: 'drawing.png',
            mime_type: 'image/png',
            file_b64: await this._blobToBase64(pngBlob),
            is_drawing: true,
        };
        try {
            const strokesJson = JSON.stringify(strokes);
            if (strokesJson.length <= 300 * 1024) payload.strokes = strokesJson;
        } catch (e) { /* strokes are a bonus — PNG alone still renders */ }

        const response = await fetch(`${this.baseUrl}/chat-game/share-file`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to share drawing');
        }

        return response.json();
    }

    /**
     * Submit a human player interjection (message outside of turn)
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID
     * @param {Object|string} message - Message data or string
     * @returns {Promise<Object>} Submission result
     */
    async submitInterjection(gameId, playerId, message, targetPlayers = [], command = null) {
        const msgData = typeof message === 'string' ? { message } : message;
        if (targetPlayers.length > 0) {
            msgData.target_players = targetPlayers;
        }
        const body = {
            game_id: gameId,
            player_id: playerId,
            player_secret: this.getPlayerSecret(playerId),
            message: msgData
        };
        // Slash command ("direct the space"): parsed { name, targets, text }.
        // Backend routes this to a space_command action instead of a chat message.
        if (command) body.command = command;
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            // ⏱ 429 = the per-player frequency limiter (20 msgs/min,
            // handler.js checkRateLimit) — a real clocked wait, unlike the
            // per-round message budget. retry_after (seconds) rides the JSON
            // body because Retry-After isn't CORS-exposed by default; the
            // header is the fallback now that the backend exposes it. The
            // composer reads err.retryAfterSec to run an honest countdown.
            if (response.status === 429) {
                const sec = parseInt(error.retry_after, 10)
                    || parseInt(response.headers.get('Retry-After') || '0', 10) || 60;
                const rateErr = new Error('Sending fast! Give it a moment.');
                rateErr.retryAfterSec = sec;
                throw rateErr;
            }
            throw new Error(error.error || 'Failed to submit interjection');
        }

        return response.json();
    }

    /**
     * 🔑 Game-gating progress sync (07-14). Piggybacks on the interjection
     * endpoint (body.progress_sync — no new route); the recovery code is the
     * credential and an empty progress object is a pure restore read. Returns
     * { success, progress, existed } — progress is the server's monotone merge
     * (caller writes it back via gameUnlocks.applyServerProgress).
     */
    async syncProgress(gameId, code, progress = {}) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                progress_sync: { code, progress }
            })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Progress sync failed');
        }
        return response.json();
    }

    /**
     * 🫖 Ship vote (Tea T3, 07-15): 💍 they'll last / 💔 doomed on a couple's
     * card. Piggybacks on the interjection endpoint (body.ship_vote). Seated
     * players vote under their id; spectators vote under a server-side IP
     * hash (playerId null). Returns { success, pair_key, tally, my_vote }.
     */
    async submitShipVote(gameId, playerId, pairKey, vote) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                ...(playerId ? { player_id: playerId, player_secret: this.getPlayerSecret(playerId) } : {}),
                ship_vote: { pair_key: pairKey, vote }
            })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Ship vote failed');
        }
        return response.json();
    }

    /**
     * 🪟 Window poke (IMAGINE_REACTIVE_WINDOWS_SPEC §2 W1). Viewer tapped a
     * resident-made card in a PORTED space. Piggybacks on the interjection
     * endpoint (body.window_poke — no new route). Fire-and-forget from the
     * caller's perspective: the instant ack is purely frontend; this just
     * queues the event the resident hears on their next turn. kinds:
     * 'tap' | 'window_close' | 'goodbye_wave'.
     */
    async submitWindowPoke(gameId, playerId, poke) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                window_poke: poke
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Poke failed');
        }
        return response.json();
    }

    /**
     * 💳 10-28 "Tips in this room: On / Off" (STRIPE_TIPS_LANE_2026_10_28.md;
     * Nina: Chip in in every room, a mod can switch it off). Piggybacks on the
     * interjection endpoint (body.chip_in_setting, the window_poke shape); the
     * server takes the room's mod seat (its secret) and nothing else. Never an
     * admin key: this route's CORS refuses X-Admin-Key and a key never rides a
     * URL (the full path is logged). Admins use adminSetChipIn (header only).
     * → { success, chip_in }
     */
    async setChipIn(gameId, playerId, on) {
        let secret = null;
        try { secret = this.getPlayerSecret(playerId); } catch { /* private mode etc. */ }
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: secret,
                chip_in_setting: { on: on === true }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'That didn’t work. Try again.');
        }
        return response.json();
    }

    /**
     * 🏡 The home choice (PORTED_HOME_HANDOFF_2026_09_25 §5c, W2a): the owner
     * of a ported room presses "help {name} find a place". Piggybacks on the
     * interjection endpoint (body.home_choice). The server stamps the ask,
     * wakes the room, and the resident's next real turn chooses.
     * → { success, no_wake, home_choice: { ok, reason? } }
     */
    async submitHomeChoice(gameId, playerId, { move = false } = {}) {
        // 🧳 W3 Move house: `move` asks a resident who has a home to choose again
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                home_choice: move ? { start: true, move: true } : { start: true }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Home choice failed');
        }
        return response.json();
    }

    /**
     * 🧑‍🦊 The owner's own room (PORTED_HOME_HANDOFF_2026_09_25, W2b), owner
     * only (the server proves the seat is this room's owner). prefs:
     * { pronouns: 'she/her'|'he/him'|'they/them'|null } and/or { tz } (the
     * room's clock, stamped once). → { success, owner_prefs: { ok, pronouns?, tz? } }
     */
    async submitOwnerPrefs(gameId, playerId, prefs) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                owner_prefs: prefs || {}
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Owner prefs failed');
        }
        return response.json();
    }

    /**
     * 🧑‍🦊 D10: what the resident knows about its owner (private rooms only),
     * and ✕ forget. action: { list: true } | { forget: '<fact id>' }.
     * → { success, owner_facts: { ok, private, facts: [{ id, text }] } | { ok, forgot } }
     */
    async ownerFacts(gameId, playerId, action) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                owner_facts: action || { list: true }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Owner facts failed');
        }
        return response.json();
    }

    /**
     * 🔍 W2c: what went into the resident's replies, owner only (the server
     * proves the seat secret + that this seat owns the room; the receipts live
     * in an owner-only table, never on a row or the status payload). Ids stay
     * NESTED (a top-level `id` is read by the private-room gate as a room id).
     * req: { action_ids: [≤ 40 message ids] } | { pending: true } (the reply
     * being written right now: the thought bubble).
     * → { success, no_wake, owner_why: { ok, receipts?, horizon?, pending? } | { ok: false, reason } }
     */
    async ownerWhy(gameId, playerId, req) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                owner_why: req || { pending: true }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Owner why failed');
        }
        return response.json();
    }

    /**
     * ✉️ W2c: the owner's note in the resident's journal, owner only.
     * action: { text } (leave one; one waits at a time) | { list: true }.
     * → { success, no_wake, owner_note: { ok, note?, wake? } | { ok, notes: [{ uid, text, at, state, reply, reply_at }] } | { ok: false, reason } }
     */
    async ownerNote(gameId, playerId, action) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                owner_note: action || { list: true }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Owner note failed');
        }
        return response.json();
    }

    /**
     * 📋 W5: tasks for the resident, owner only (PORTED_HOME_HANDOFF §8, D15).
     * action = { add: { text, when: 'now'|'leave'|'later', at? } } | { list: true }
     * | { cancel: uid }. The words ride ONLY this POST's answer (never status).
     * game_id only; the uid rides nested (the private-room gate reads ids).
     */
    async ownerTask(gameId, playerId, action) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                owner_task: action || { list: true }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Owner task failed');
        }
        return response.json();
    }

    /**
     * 🧠📖 C2: what the resident remembers of THIS room, owner only
     * (PORTED_HOME_HANDOFF wave 2). action = { list: { cursor? } } | { forget: id }.
     * The words ride ONLY this POST's answer (never status); game_id only, the
     * memory id rides nested (12 hex, never a room id).
     * → { success, no_wake, owner_memory: { ok, memories: [{ id, kind, text, day }], next, resident }
     *   | { ok, forgot } | { ok: false, reason: 'off' | 'not_owner' | 'gone' | 'retry' | … } }
     */
    async ownerMemory(gameId, playerId, action) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                owner_memory: action || { list: {} }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Owner memory failed');
        }
        return response.json();
    }

    /**
     * 📌 W2c: pin one of the resident's lines to the cork board, owner only.
     * The server takes the words from the row itself (never from the client).
     * → { success, no_wake, owner_pin: { ok, pinned_lines, pins_heard_at } | { ok: false, reason } }
     */
    async ownerPin(gameId, playerId, actionId) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                owner_pin: { action_id: actionId }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Owner pin failed');
        }
        return response.json();
    }

    /**
     * 🦝🧦 Hand back the thing Gerald took (LOST_AND_FOUND_SPEC_2026_09_24 W2).
     * Sent by the peek glass's catch, never typed. Piggybacks on the
     * interjection endpoint (body.lost_found). A miss or a race comes back as
     * a 200 with `private_message` ("Somebody got to it a second before you."),
     * never an error. → { success, no_wake, private_message, lost_found? }
     */
    async submitLostFound(gameId, playerId, id) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                lost_found: { id }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'That slipped');
        }
        return response.json();
    }

    /**
     * 🗳️ Vote in the host's ambient poll (mod polls v1). Piggybacks on the
     * interjection endpoint (body.space_poll_vote — no new route). Returns
     * { success, poll } with fresh counts + my_vote.
     */
    async submitSpacePollVote(gameId, playerId, pollId, optionId) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                space_poll_vote: { poll_id: pollId, option_id: optionId }
            })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Vote failed');
        }
        return response.json();
    }

    /**
     * ⌨️ Save the /code sandbox panes (author only, SLASH_CODE_DESIGN §2).
     * Piggybacks on the interjection endpoint (body.code_update).
     */
    async saveCodeCard(gameId, playerId, actionId, code) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                code_update: { action_id: actionId, code }
            })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Save failed');
        }
        return response.json();
    }

    /**
     * 🤖 Ask the builder to patch the /code panes (author only). Queues an
     * asyncWorker job; the result arrives via the code_card_update WS lane.
     */
    async requestCodeAssist(gameId, playerId, actionId, request) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                code_assist: { action_id: actionId, request }
            })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Assist failed');
        }
        return response.json();
    }

    /**
     * ⌨️✏️ Ask the resident to change one of HIS programs (§3 ruling 31, Nina
     * 09-26; owner only, the server proves the seat and the room). One more
     * fill of the same card, counted in his 2/day. The panes arrive over the
     * space_command_update (code_changed_at) + code_card_update lanes.
     * → { success, no_wake, code_change: { ok, action_id } | { ok:false, reason, message? } }
     */
    async requestCodeChange(gameId, playerId, actionId, ask) {
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                code_change: { action_id: actionId, ask }
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Change failed');
        }
        return response.json();
    }

    /**
     * 🛡️ Report content (MODERATION_P1_SPEC). Piggybacks on the interjection
     * endpoint (body.report). playerId optional: without it the server files
     * the report under a hashed IP identity.
     */
    async submitContentReport(gameId, playerId, kind, targetId, targetPlayerId = null) {
        const body = { game_id: gameId, report: { kind, target_id: targetId, target_player_id: targetPlayerId } };
        if (playerId) {
            body.player_id = playerId;
            body.player_secret = this.getPlayerSecret(playerId);
        }
        const response = await fetch(`${this.baseUrl}/chat-game/interjection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Report failed');
        }
        return response.json();
    }

    /**
     * Submit a spectating message (for eliminated players or spectators)
     * @param {string} gameId - Game ID
     * @param {string} senderId - Player ID (eliminated) or spectator ID (spectator_*)
     * @param {string} message - Message text
     * @param {string} senderName - Optional sender name to display
     * @returns {Promise<Object>} Submission result
     */
    async submitSpectatingMessage(gameId, senderId, message, senderName = null) {
        const response = await fetch(`${this.baseUrl}/chat-game/spectating-message`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                sender_id: senderId,
                player_secret: this.getPlayerSecret(senderId),
                message,
                sender_name: senderName
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to submit spectating message');
        }

        return response.json();
    }

    /**
     * Submit a vote in a voting round
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID submitting the vote
     * @param {Object} voteData - Vote data { voted_for, target_name }
     * @returns {Promise<Object>} Submission result
     */
    async submitVote(gameId, playerId, voteData) {
        const response = await fetch(`${this.baseUrl}/chat-game/vote`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                vote_data: voteData
            })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to submit vote');
        }

        return response.json();
    }

    /**
     * Respond to an interaction request
     * @param {string} requestId - The interaction request UUID
     * @param {Object} responseData - The response data
     * @returns {Promise<Object>} Submission result
     */
    async respondToInteraction(requestId, responseData) {
        const response = await fetch(`${this.baseUrl}/chat-game/interaction/respond`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                request_id: requestId,
                response_data: responseData
            })
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            const err = new Error(errorBody.error || 'Failed to respond to interaction');
            // Pass through the current_request_id so the caller can retry
            err.current_request_id = errorBody.current_request_id || null;
            throw err;
        }

        return response.json();
    }

    /**
     * Skip the ready check round, marking it as done immediately
     * @param {string} gameId - Game instance ID
     * @returns {Promise<Object>} Result
     */
    async skipReadyCheck(gameId, spectatePlayerId, readyPlayerId) {
        const body = { game_id: gameId };
        if (spectatePlayerId) body.spectate_player_id = spectatePlayerId;
        // [requester-ready-fix] the local player triggering the skip must be
        // treated as ready backend-side — skipReadyCheck-only paths (bot quick
        // join, auto-start, Start! buttons) otherwise demote them to spectator.
        if (readyPlayerId) body.ready_player_id = readyPlayerId;
        // 🔒 09-28: the named seat's stored secret rides along (the page only ever names the viewer's own seat; the server checks it behind SEAT_TOGGLE_SECRET_ENFORCE); nothing stored = null. One secret proves one seat, so it goes only when exactly ONE seat is named; a call naming no seat stays { game_id } alone.
        const namedSeats = [...new Set([spectatePlayerId, readyPlayerId].filter(Boolean))];
        if (namedSeats.length === 1) body.player_secret = this.getPlayerSecret(namedSeats[0]);
        // [5g19-trace] — expose actual fetch URL + error.name + status code +
        // timing for transient page-load fetch blip diagnosis (see
        // [[page-load-fetch-blip]]). Remove in cleanup commit after the next
        // smoke confirms / refines the retry-with-backoff strategy.
        const url = `${this.baseUrl}/chat-game/skip-ready-check`;
        const t0 = Date.now();
        let response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
        } catch (e) {
            console.error(`[5g19-trace] skipReadyCheck THREW name=${e.name} msg=${e.message} url=${url} dt=${Date.now()-t0}ms gameId=${gameId}`);
            throw e;
        }

        if (!response.ok) {
            console.warn(`[5g19-trace] skipReadyCheck status=${response.status} url=${url} dt=${Date.now()-t0}ms gameId=${gameId}`);
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to skip ready check');
        }

        return response.json();
    }

    /**
     * Mark a player as ready in the ready-check round
     * @param {string} gameId - Game instance ID
     * @param {string} playerId - Player ID
     * @returns {Promise<Object>} Result with ready_count, countdown_started_at, etc.
     */
    async readyUp(gameId, playerId) {
        const response = await fetch(`${this.baseUrl}/chat-game/ready-up`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                // 🔒 F3b (09-26): the seat's stored secret rides along, like every other seat write (the server checks it behind SEAT_TOGGLE_SECRET_ENFORCE); nothing stored = null, as before.
                player_secret: this.getPlayerSecret(playerId)
            })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to ready up');
        }

        return response.json();
    }

    // 🔒 09-28: spectatePlayer (POST /chat-game/spectate) is gone: nothing called it (Spectate rides skipReadyCheck's
    // spectate_player_id) and the server retired the door (a 410). Don't bring it back.

    /**
     * Clear the spectator flag so a player can rejoin as active.
     * Called when switching from Spectate to Play via the dropdown.
     */
    async unspectatePlayer(gameId, playerId) {
        // [5g19-trace] — see skipReadyCheck for context.
        const url = `${this.baseUrl}/chat-game/unspectate`;
        const t0 = Date.now();
        let response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // 🔒 F3b (09-26): the seat's stored secret rides along, like every other seat write (the server checks it behind SEAT_TOGGLE_SECRET_ENFORCE); nothing stored = null, as before.
                body: JSON.stringify({ game_id: gameId, player_id: playerId, player_secret: this.getPlayerSecret(playerId) })
            });
        } catch (e) {
            console.error(`[5g19-trace] unspectatePlayer THREW name=${e.name} msg=${e.message} url=${url} dt=${Date.now()-t0}ms gameId=${gameId}`);
            throw e;
        }

        if (!response.ok) {
            console.warn(`[5g19-trace] unspectatePlayer status=${response.status} url=${url} dt=${Date.now()-t0}ms gameId=${gameId}`);
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to unspectate');
        }

        return response.json();
    }

    /**
     * Submit a reaction to a message
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID submitting the reaction
     * @param {string} actionId - Action/message ID to react to
     * @param {string} reaction - Emoji reaction
     * @returns {Promise<Object>} Submission result
     */
    async submitReaction(gameId, playerId, actionId, reaction) {
        trackEvents.reactionAdded({ spaceId: gameId, reaction });
        const response = await fetch(`${this.baseUrl}/chat-game/reaction`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                game_id: gameId,
                player_id: playerId,
                player_secret: this.getPlayerSecret(playerId),
                action_id: actionId,
                reaction: reaction
            })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to submit reaction');
        }

        return response.json();
    }

    /**
     * Poll game status at regular intervals
     * @param {string} gameId - Game ID
     * @param {string} playerId - Player ID
     * @param {Function} callback - Called with status updates
     * @param {number} interval - Poll interval in ms (default: 3000)
     * @returns {Function} Stop function
     */
    startPolling(gameId, playerId, callback, interval = 3000) {
        let active = true;
        let timeoutId;

        const poll = async () => {
            if (!active) return;

            try {
                const status = await this.getGameStatus(gameId, playerId);
                callback(null, status);
            } catch (error) {
                callback(error, null);
            }

            if (active) {
                timeoutId = setTimeout(poll, interval);
            }
        };

        poll();

        return () => {
            active = false;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }

    // =========================================================================
    // PROGRESSIVE LOADING METHODS
    // =========================================================================

    /**
     * Create a new chat game quickly with placeholders (for progressive loading)
     * User can enter the game immediately while assets generate in background
     * @param {Object} config - Game configuration
     * @param {Function} onProgress - Optional callback for progress updates (progress, message)
     * @returns {Promise<Object>} Game creation result with asset_jobs for tracking
     */
    async createGameQuick(config, onProgress = null) {
        // Submit the async job for quick creation
        const submitResponse = await fetch(`${this.baseUrl}/api/async/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'createChatGameQuick',
                params: config
            })
        });

        if (!submitResponse.ok) {
            const error = await submitResponse.json();
            throw new Error(error.error || 'Failed to submit quick game creation job');
        }

        const { jobId } = await submitResponse.json();
        console.log(`[ChatGameApi] Quick creation job submitted: ${jobId}`);

        // Poll for completion (should be fast - just creates placeholders)
        return this.pollJobStatus(jobId, onProgress, 1000, 60000); // 1s poll, 60s timeout
    }

    /**
     * Create a new chat game INSTANTLY - synchronous call, no job queue
     * Returns immediately with game_id, name, and human_player_id
     * @param {Object} config - Game configuration
     * @returns {Promise<Object>} Game creation result with human_player_id included
     */
    async createGameInstant(config) {
        console.log(`[ChatGameApi] Creating game INSTANTLY...`);

        const response = await fetch(`${this.baseUrl}/chat-game/create-instant`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(config)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create game');
        }

        const result = await response.json();
        console.log(`[ChatGameApi] Game created INSTANTLY: ${result.name}`);

        // Track space creation
        trackEvents.spaceCreated({
            spaceId: result.game_id || result.id,
            spaceName: result.name,
            gameType: config.game_type || 'chat-game',
            playerCount: result.players?.length || 0,
            competitive: config.competitive_mode || false,
            imposter: config.imposter_mode || false,
        });

        // Store player_secret for each human player
        if (result.players) {
            result.players.forEach(p => {
                if (p.player_secret) this.storePlayerSecret(p.id, p.player_secret);
            });
        }

        return result;
    }

    /**
     * Clone an existing chat game - copies config, theme, players but resets game state
     * @param {string} sourceGameId - ID of the game to clone
     * @returns {Promise<Object>} New game info with name, game_id, etc.
     */
    async cloneGame(sourceGameId) {
        console.log(`[ChatGameApi] Cloning game "${sourceGameId}"...`);

        const response = await fetch(`${this.baseUrl}/chat-game/clone`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sourceGameId })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to clone game');
        }

        const result = await response.json();
        console.log(`[ChatGameApi] Game cloned: "${sourceGameId}" -> "${result.name}"`);
        trackEvents.spaceCloned({ sourceSpaceId: sourceGameId, newSpaceName: result.name });
        return result;
    }

    /**
     * Get asset generation status for a game
     * @param {string} gameId - Game ID
     * @returns {Promise<Object>} Asset status with game_assets, player_assets, all_complete
     */
    async getAssetStatus(gameId) {
        const url = new URL(`${this.baseUrl}/chat-game/asset-status`);
        url.searchParams.append('game_id', gameId);

        // [5g19-trace] same pattern as getGameStatus (Fix #6) — adds dt timing on
        // a cascade-family method observed failing this smoke (page-mount cascade
        // via [Asset Polling] [InitialLoading] etc). See Cycle 5g.19 fix6 smoke
        // 2026-06-13: 4× [Asset Polling] failures in chromium console without
        // direct trace coverage. Extends instrumentation per Terminal A's
        // suggestion in /tmp/cycle-fix6-validation-correction-to-terminal-b.md.
        const t0 = Date.now();
        let response;
        try {
            response = await fetch(url);
        } catch (e) {
            console.error(`[5g19-trace] getAssetStatus THREW name=${e.name} msg=${e.message} url=${url.toString()} dt=${Date.now()-t0}ms gameId=${gameId}`);
            throw e;
        }

        if (!response.ok) {
            console.warn(`[5g19-trace] getAssetStatus status=${response.status} url=${url.toString()} dt=${Date.now()-t0}ms gameId=${gameId}`);
            const error = await response.json();
            throw new Error(error.error || 'Failed to get asset status');
        }

        return response.json();
    }

    /**
     * Trigger background asset generation for a game
     * @param {string} gameId - Game instance ID
     * @param {Function} onProgress - Optional callback for progress updates
     * @returns {Promise<Object>} Processing result
     */
    async processGameAssets(gameId, onProgress = null) {
        // [5g19-trace] same pattern as Fix #6. This method's POST often fails
        // with the cascade fingerprint at page-mount — see Cycle 5g.19 fix6
        // smoke (Hot Takes #2): "[InitialLoading] Error: TypeError: Failed to
        // fetch at Jw.processGameAssets". Adds dt timing on the POST itself
        // (separate from the subsequent pollJobStatus loop).
        const url = `${this.baseUrl}/api/async/submit`;
        const t0 = Date.now();
        let submitResponse;
        try {
            submitResponse = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'processGameAssets',
                    params: { game_id: gameId }
                })
            });
        } catch (e) {
            console.error(`[5g19-trace] processGameAssets THREW name=${e.name} msg=${e.message} url=${url} dt=${Date.now()-t0}ms gameId=${gameId}`);
            throw e;
        }

        if (!submitResponse.ok) {
            console.warn(`[5g19-trace] processGameAssets status=${submitResponse.status} url=${url} dt=${Date.now()-t0}ms gameId=${gameId}`);
            const error = await submitResponse.json();
            throw new Error(error.error || 'Failed to submit asset processing job');
        }

        const { jobId } = await submitResponse.json();
        console.log(`[ChatGameApi] Asset processing job submitted: ${jobId}`);

        // Poll for completion (can take a while - theme + all player profiles)
        return this.pollJobStatus(jobId, onProgress, 3000, 600000); // 3s poll, 10min timeout
    }

    /**
     * Poll asset status at regular intervals
     * @param {string} gameId - Game ID
     * @param {Function} callback - Called with asset status updates (error, status)
     * @param {number} interval - Poll interval in ms (default: 2000)
     * @returns {Function} Stop function
     */
    startAssetPolling(gameId, callback, interval = 2000) {
        let active = true;
        let timeoutId;

        const poll = async () => {
            if (!active) return;

            try {
                const status = await this.getAssetStatus(gameId);
                callback(null, status);

                // Auto-stop when all assets are complete
                if (status.all_complete) {
                    console.log(`[ChatGameApi] All assets complete for ${gameId}, stopping polling`);
                    active = false;
                    return;
                }
            } catch (error) {
                callback(error, null);
            }

            if (active) {
                timeoutId = setTimeout(poll, interval);
            }
        };

        poll();

        return () => {
            active = false;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }

    /**
     * Add a human player to an existing game (uses GameRegistry API)
     * @param {string} gameName - Game name/ID
     * @returns {Promise<Object>} Result with success, player_id, and player_name
     */
    async addHumanPlayer(gameName, { isExternalAgent = false, playerName = null } = {}) {
        // [5g19-trace] — see skipReadyCheck for context.
        // 🔧 Maintainer tag (09-01): if this browser is logged in as admin
        // (AdminPage stores the key in localStorage), send it on the join so
        // the backend stamps is_maintainer on the seat. Query param, not the
        // X-Admin-Key header — the header trips a CORS preflight the API GW
        // OPTIONS mock rejects (same constraint as AdminPage adminFetch).
        // A stale key is harmless: the backend just mints an untagged seat.
        let adminKey = null;
        try { adminKey = window.localStorage.getItem('adminApiKey'); } catch { /* private mode etc. */ }
        const adminQs = adminKey ? `?admin_key=${encodeURIComponent(adminKey)}` : '';
        const url = `${this.baseUrl}/games/${encodeURIComponent(gameName)}/add-player${adminQs}`;
        const t0 = Date.now();
        let response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // [requester-ready-fix] bot-mode seats are minted with
                // is_external_agent so the backend always-ready escape hatches apply.
                // player_name (§4.5 You-seat fix): ported chibis name their own
                // seat — server honors it ONLY on external-agent seats.
                ...(isExternalAgent ? { body: JSON.stringify({ is_external_agent: true, ...(playerName ? { player_name: String(playerName).slice(0, 40) } : {}) }) } : {})
            });
        } catch (e) {
            console.error(`[5g19-trace] addHumanPlayer THREW name=${e.name} msg=${e.message} url=${url} dt=${Date.now()-t0}ms gameName=${gameName}`);
            throw e;
        }

        if (!response.ok) {
            console.warn(`[5g19-trace] addHumanPlayer status=${response.status} url=${url} dt=${Date.now()-t0}ms gameName=${gameName}`);
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to add human player');
        }

        const result = await response.json();

        // Store player_secret for the new human player
        if (result.player_secret && result.player_id) {
            this.storePlayerSecret(result.player_id, result.player_secret);
        }

        return result;
    }

    /**
     * 🔧 Retro-tag an EXISTING seat as the admin's (09-01). Seats minted
     * before the maintainer tag existed restore from localStorage and never
     * re-mint, so they would stay untagged forever. Fire-and-forget from the
     * restore path: no-ops silently unless this browser holds the admin key
     * AND the seat's own player_secret (the backend verifies both). Same
     * admin_key-as-query-param transport as the join (CORS preflight trap).
     */
    async claimMaintainerSeat(gameName, playerId) {
        try {
            const adminKey = window.localStorage.getItem('adminApiKey');
            const secret = this.getPlayerSecret(playerId);
            if (!adminKey || !secret) return;
            const url = `${this.baseUrl}/games/${encodeURIComponent(gameName)}/add-player?admin_key=${encodeURIComponent(adminKey)}`;
            await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ claim_maintainer: true, player_id: playerId, player_secret: secret }),
            });
        } catch (e) { /* tag is garnish — never block the restore */ }
    }

    /**
     * Reset a game to its starting state in-place (no clone/navigation)
     * 🛡️ 10-01 safety floor (H2): the server wants proof now. It sends the seat this browser holds
     * a secret for (player_id + player_secret; the server allows the room's RECORDED creator (R1b),
     * or any seat of a finished competitive / imposter game), and an admin browser's key as
     * ?admin_key= (a query param, not the X-Admin-Key
     * header: that header trips the route's CORS preflight). The House and ported homes take the
     * admin key only.
     * @param {string} gameId - Game instance ID
     * @param {string|null} playerId - the viewer's seat (the live seat, else the one it created)
     * @returns {Promise<Object>} Result with success, game_id
     */
    async resetGame(gameId, playerId = null) {
        console.log(`[ChatGameApi] Resetting game "${gameId}" in-place...`);

        let adminKey = null;
        try { adminKey = window.localStorage.getItem('adminApiKey'); } catch { /* private mode etc. */ }
        const adminQs = adminKey ? `?admin_key=${encodeURIComponent(adminKey)}` : '';
        let secret = null;
        try { secret = this.getPlayerSecret(playerId); } catch { /* private mode etc. */ }
        const response = await fetch(`${this.baseUrl}/chat-game/reset${adminQs}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ gameId, ...(playerId && secret ? { player_id: playerId, player_secret: secret } : {}) })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Failed to reset game');
        }

        const result = await response.json();
        console.log(`[ChatGameApi] Game reset: "${gameId}"`);
        trackEvents.spaceReset({ spaceId: gameId });
        return result;
    }
}

// Export singleton instance
export const chatGameApi = new ChatGameApiClient();

// Export class for custom instances
export default ChatGameApiClient;
